import { useMemo } from "react";
import { ModalV2, Button, Text, Flex } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import * as styles from "./styles_vanilla.css";
import { type ChangeDetails, type ResetDocumentDataWarningModalProps } from "./types";

const getFieldLabel = (changeType: ChangeDetails["changeType"]): string => {
  const labels: Record<ChangeDetails["changeType"], string> = {
    profile: "related profile",
    documentType: "document type",
    subType: "document sub-type",
  };
  return labels[changeType];
};

const getModalTitle = (changeType: ChangeDetails["changeType"]): string => {
  const titles: Record<ChangeDetails["changeType"], string> = {
    profile: "Change related profile",
    documentType: "Change document type",
    subType: "Change document sub-type",
  };
  return titles[changeType];
};

export const ResetDocumentDataWarningModal = ({
  show,
  onHide,
  onConfirm,
  changeDetails,
  isLoading = false,
}: ResetDocumentDataWarningModalProps) => {
  const fieldLabel = useMemo(
    () => (changeDetails ? getFieldLabel(changeDetails.changeType) : ""),
    [changeDetails],
  );

  const modalTitle = useMemo(
    () => (changeDetails ? getModalTitle(changeDetails.changeType) : ""),
    [changeDetails],
  );

  if (!changeDetails) {
    return null;
  }

  return (
    <ModalV2
      show={show}
      onHide={onHide}
      backdrop={true}
      closeOnClickOutside={false}
      contentClassName={styles.modal}
      title={modalTitle}
    >
      <ModalV2.Body>
        <Text>
          Are you sure you want to change the {fieldLabel} of {changeDetails.documentName}?
        </Text>
        <Flex className={styles.changeComparisonContainer} justifyContent="space-between">
          <Flex className={styles.changeColumn}>
            <Text variant="secondary-md" className={styles.changeLabel}>
              Current
            </Text>
            <Text variant="regular-md">{changeDetails.currentValue || "--"}</Text>
          </Flex>
          <Flex className={styles.changeColumn}>
            <Text variant="secondary-md" className={styles.changeLabel}>
              New
            </Text>
            <Text variant="regular-md">{changeDetails.newValue || "--"}</Text>
          </Flex>
        </Flex>
        <Text variant="secondary-md" className={styles.infoText}>
          Changing the {fieldLabel} will reset the information previously entered for this
          document.
        </Text>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex gap={Sizes.MD} justifyContent="flex-end">
          <Button onClick={onHide} variant="neutral" disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            variant="danger"
            isLoading={isLoading}
            disabled={isLoading}
          >
            Change and reset
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
